import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CheckCircle2, ClipboardList, Clock, History, MessageSquareText } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/utils/formatters";
import { usePartnerWorkLogs } from "@/hooks/usePartnerData";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { getFileUrl } from "@/lib/api-client";

import ActivityCalendar, { CalendarAsideCard } from "@/components/shared/ActivityCalendar";
import { toneForStatus } from "@/components/shared/EventChip";
import { latestLogs, relativeDay, workLogEvents, workLogLegend } from "@/components/shared/workLogCalendar";

export const Route = createFileRoute("/partner/work-logs/")({ component: PartnerWorkLogs });

function PartnerWorkLogs() {
  const [search, setSearch] = useState("");
  const { data: logsData = [], isLoading } = usePartnerWorkLogs();

  const pendingLogs = logsData.filter((l: any) => l.status === "pending");
  const totalHours = logsData.reduce((sum: number, l: any) => sum + (l.hours || 0), 0);

  const filteredLogs = logsData.filter((l: any) => {
    const studentName = l.studentName || "";
    return studentName.toLowerCase().includes(search.toLowerCase());
  });

  const calendarEvents = useMemo(
    () =>
      workLogEvents(
        logsData as any[],
        (l: any) => l.studentName || "Intern",
        (l: any) => `/partner/work-logs/${l.id}`,
      ),
    [logsData],
  );

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Journal reviews"
        subtitle="Verify industrial training work logs, monitor daily tasks, and certify logged hours for your interns."
        breadcrumbs={[{ label: "Dashboard", path: "/partner/dashboard" }, { label: "Work Logs" }]}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total submissions" value={logsData.length} icon={<History />} />
        <StatCard label="Awaiting verification" value={pendingLogs.length} icon={<Clock />} />
        <StatCard label="Total certified hours" value={totalHours} icon={<CheckCircle2 />} />
      </div>

      <ActivityCalendar
        title="Intern activity"
        events={calendarEvents}
        legend={workLogLegend(logsData as any[])}
        asideTitle="Latest submissions"
        aside={
          logsData.length === 0 ? (
            <p className="text-sm text-muted-foreground">Submitted work logs will appear here.</p>
          ) : (
            <div className="space-y-2.5">
              {latestLogs(logsData as any[]).map((l: any) => (
                <CalendarAsideCard
                  key={l.id}
                  tone={l.status === "changes_requested" ? "lilac" : toneForStatus(l.status)}
                  title={l.studentName || "Intern"}
                  meta={`${formatDate(l.date)} · ${l.hours ?? 0}h`}
                  badge={relativeDay(l.date)}
                  to={`/partner/work-logs/${l.id}`}
                />
              ))}
            </div>
          )
        }
      />

      <SectionCard
        title="Verification ledger"
        actions={
          <SearchInput placeholder="Search by intern name..." value={search} onChange={setSearch} />
        }
        noPadding
      >
        {filteredLogs.length === 0 ? (
          <EmptyState
            bare
            icon={<ClipboardList />}
            title="No work logs"
            description={
              search ? "No work logs match your search." : "Submitted work logs will appear here."
            }
          />
        ) : (
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Intern identity</TableHead>
                <TableHead>Fulfillment date</TableHead>
                <TableHead>Operating window</TableHead>
                <TableHead>Intensity</TableHead>
                <TableHead>Verification status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((l: any) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="size-9">
                        <AvatarImage src={getFileUrl(l.studentAvatar || l.avatar)} alt="" />
                        <AvatarFallback className="bg-pastel-lavender text-xs text-pastel-lavender-ink">
                          {(l.studentName || "S").charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-foreground">{l.studentName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-body tabular-nums">{formatDate(l.date)}</TableCell>
                  <TableCell className="font-mono text-caption text-muted-foreground tabular-nums">
                    {l.timeStart} - {l.timeEnd}
                  </TableCell>
                  <TableCell className="font-mono text-foreground tabular-nums">{l.hours}h</TableCell>
                  <TableCell>
                    <StatusChip status={l.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant={l.status === "pending" ? "secondary" : "ghost"}
                      asChild
                    >
                      <Link to={`/partner/work-logs/${l.id}` as any}>
                        <MessageSquareText />
                        {l.status === "pending" ? "Verify" : "Review"}
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
